import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updatePerson } from '@/api/people';
import { getOnsiteLiveAdminStatus, getOnsiteLiveUser, listOnsiteLiveUsers, updateOnsiteLiveUser } from '@/api/onsiteLiveAdmin';
import { listOffboardingStatus } from '@/api/offboardingStatus';
import { getMigrationForPerson } from '@/api/onedriveMigration';
import { useAuth } from '@/auth/useAuth';
import { formatDateTime, formatWallClock } from '@/lib/dateFormat';
import type { OffboardingStatusRow, OnsiteLiveUser, Person } from '@/types/oscar';
import { leavingSchema, leavingToForm, toLeavingPayload, type LeavingFormData } from '../../OnsiteLiveAdmin/schema';
import { OnsiteLiveUserPicker } from '../../OnsiteLiveAdmin/OnsiteLiveUserPicker';
import { MailRecipientPicker } from '../../OnsiteLiveAdmin/MailRecipientPicker';
import { OffboardingTrail, SignalPill, overallBadge } from '../../Offboarding/OffboardingStatus';
import { ErrorBanner, FieldError } from '../../BranchRelationships/shared';
import { btnPrimary, inputCls } from '../../BranchRelationships/styles';
import { PersonOneDriveStatus } from './PersonOneDrive';
import { dateSchema, terminationValue } from './leavingDate';

/**
 * Person › Leaving (Anthony, 25/10/2026: "termination date, email divert and
 * replacement manager move there, with the person's offboarding results").
 * Everything needed on the day, in one place, and settable beforehand:
 *  - the termination date (oscar.Person; OnsiteLive's expiry follows it);
 *  - email divert and replacement manager (the person's OnsiteLive user);
 *  - offboarding progress: the Offboarding status row, its history, OneDrive.
 * The date is admin + helpdesk (as on the Profile before); the OnsiteLive
 * settings are admin, after the cutover - the same rules as the OnsiteLive tab.
 */
export function PersonLeaving({ person }: { person: Person }) {
  const { canMutate, isAdmin } = useAuth();
  return (
    <div className="space-y-4">
      <LeavingDate person={person} canEdit={canMutate} />
      {canMutate ? (
        <LeavingOnsiteLive personId={person.PersonId} isAdmin={isAdmin} />
      ) : (
        <p className="text-sm text-darkGrey/70">The email divert and offboarding progress are shown to admin and helpdesk.</p>
      )}
      <PersonOneDriveStatus personId={person.PersonId} isAdmin={isAdmin} active={person.Active} />
    </div>
  );
}

// ── Termination date ──────────────────────────────────────────────────────────

type DateFormData = z.infer<typeof dateSchema>;

function LeavingDate({ person, canEdit }: { person: Person; canEdit: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const current = person.TerminationDateTime ? person.TerminationDateTime.slice(0, 10) : '';
  // A stored midnight is "no time".
  const storedTime = person.TerminationDateTime ? person.TerminationDateTime.slice(11, 16) : '';
  const currentTime = storedTime === '00:00' ? '' : storedTime;
  const { register, handleSubmit, reset, formState } = useForm<DateFormData>({
    resolver: zodResolver(dateSchema),
    defaultValues: { TerminationDate: current, TerminationTime: currentTime },
  });
  useEffect(() => { reset({ TerminationDate: current, TerminationTime: currentTime }); }, [current, currentTime, reset]);

  const save = useMutation({
    // An absent date means "leave alone" server-side, so clearing has to be said.
    mutationFn: (d: DateFormData) =>
      updatePerson(person.PersonId, d.TerminationDate
        ? { TerminationDateTime: terminationValue(d.TerminationDate, d.TerminationTime) }
        : { ClearTerminationDate: true }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['person', person.PersonId] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the termination date.'),
  });

  return (
    <form
      className="rounded border border-border bg-white p-4"
      aria-label="Leaving date"
      onSubmit={(e) => { e.preventDefault(); void handleSubmit((d) => save.mutate(d))(); }}
    >
      <h2 className="font-heading text-lg font-bold text-navy">Leaving date</h2>
      <p className="mt-1 text-sm text-darkGrey/80">
        Setting a date marks an active person as Offboarding. OnsiteLive&apos;s expiry follows within 15 minutes, and from the
        time given (20:00 when no time is given) offboarding disables the accounts and runs the email divert below. Times are
        Sydney time.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Termination date
          <input type="date" {...register('TerminationDate')} disabled={!canEdit || save.isPending} className={`${inputCls} mt-1 font-normal`} />
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Time (Sydney, optional)
          <input type="time" {...register('TerminationTime')} disabled={!canEdit || save.isPending} className={`${inputCls} mt-1 font-normal`} />
        </label>
        {canEdit && (
          <button type="submit" className={btnPrimary} disabled={save.isPending || !formState.isDirty}>
            {save.isPending ? 'Saving…' : 'Save date'}
          </button>
        )}
        <span className="text-xs text-darkGrey/70">
          Status: {person.AccountStatus}
          {person.TerminationDateTime && ` · recorded ${formatDateTime(person.TerminationDateTime)}`}
        </span>
      </div>
      {formState.errors.TerminationDate && <FieldError message={formState.errors.TerminationDate.message ?? ''} />}
      {formState.errors.TerminationTime && <FieldError message={formState.errors.TerminationTime.message ?? ''} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {save.isSuccess && !save.isPending && <p role="status" className="mt-2 text-xs text-green">Saved.</p>}
    </form>
  );
}

// ── OnsiteLive: email divert, replacement manager, offboarding progress ───────

function LeavingOnsiteLive({ personId, isAdmin }: { personId: number; isAdmin: boolean }) {
  const listQuery = useQuery({
    queryKey: ['onsitelive-admin', 'users', { personId }],
    queryFn: ({ signal }) => listOnsiteLiveUsers({ personId }, signal),
  });
  const statusQuery = useQuery({ queryKey: ['onsitelive-admin', 'status'], queryFn: ({ signal }) => getOnsiteLiveAdminStatus(signal) });
  const item = listQuery.data?.[0];
  const userQuery = useQuery({
    queryKey: ['onsitelive-admin', 'user', item?.OnsiteLiveUserId],
    queryFn: ({ signal }) => getOnsiteLiveUser(item!.OnsiteLiveUserId, signal),
    enabled: item != null,
  });

  if (listQuery.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (listQuery.isError) return <p className="text-sm text-red">Could not load the OnsiteLive account.</p>;
  if (!item) {
    return (
      <p className="rounded border border-dashed border-border bg-white px-4 py-3 text-sm text-darkGrey/80">
        No OnsiteLive user is linked to this person, so there is no email divert, replacement manager or offboarding run to show.
      </p>
    );
  }
  if (userQuery.isLoading) return <p className="text-sm text-darkGrey/70">Loading {item.Username}…</p>;
  if (userQuery.isError || !userQuery.data) return <p className="text-sm text-red">Could not load {item.Username}.</p>;

  const user = userQuery.data;
  return (
    <>
      <LeavingSettings user={user} canWrite={isAdmin && statusQuery.data?.WritesOpen === true} isAdmin={isAdmin} />
      <OffboardingProgress personId={personId} userId={user.OnsiteLiveUserId} username={user.Username} />
    </>
  );
}

function LeavingSettings({ user, canWrite, isAdmin }: { user: OnsiteLiveUser; canWrite: boolean; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Both fields are pickers (setValue), so nothing is register()ed.
  const { handleSubmit, reset, setValue, watch, formState } = useForm<LeavingFormData>({
    resolver: zodResolver(leavingSchema),
    defaultValues: leavingToForm(user),
  });
  useEffect(() => { reset(leavingToForm(user)); }, [user, reset]);
  const replacementId = watch('ReplacementManagerUserId');
  const divert = watch('EmailDivert');

  const save = useMutation({
    mutationFn: (d: LeavingFormData) => updateOnsiteLiveUser(user.OnsiteLiveUserId, toLeavingPayload(user, d)),
    onSuccess: (u) => {
      setError(null);
      qc.setQueryData(['onsitelive-admin', 'user', u.OnsiteLiveUserId], u);
      void qc.invalidateQueries({ queryKey: ['onsitelive-admin', 'users'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save.'),
  });

  return (
    <form
      className="rounded border border-border bg-white p-4"
      aria-label="Email divert and replacement manager"
      noValidate
      onSubmit={(e) => { e.preventDefault(); void handleSubmit((d) => save.mutate(d))(); }}
    >
      <h2 className="font-heading text-lg font-bold text-navy">
        Email and handover <span className="ml-2 font-mono text-xs font-normal text-darkGrey/60">OnsiteLive {user.Username}</span>
      </h2>
      {!isAdmin && <p className="mt-1 text-xs text-darkGrey/70">Read only — changed by an Oscar admin.</p>}
      <fieldset disabled={!canWrite || save.isPending} className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <MailRecipientPicker
          value={divert}
          onChange={(v) => setValue('EmailDivert', v, { shouldDirty: true, shouldValidate: true })}
          canWrite={canWrite}
          hint="On the leaving date their mail is forwarded here: a mailbox, shared mailbox or group in Entra. Leave it blank to lock the mailbox to IT instead."
          error={formState.errors.EmailDivert?.message}
        />
        <OnsiteLiveUserPicker
          label="Replacement manager"
          value={replacementId}
          valueLabel={user.ReplacementManagerName}
          onChange={(v) => setValue('ReplacementManagerUserId', v, { shouldDirty: true })}
          canWrite={canWrite}
          excludeId={user.OnsiteLiveUserId}
          hint="Who takes over from this person in OnsiteLive."
          error={formState.errors.ReplacementManagerUserId?.message}
        />
      </fieldset>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {canWrite && (
        <div className="mt-4 flex items-center gap-3">
          <button type="submit" className={btnPrimary} disabled={save.isPending || !formState.isDirty}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {save.isSuccess && !save.isPending && <span role="status" className="text-xs text-green">Saved.</span>}
        </div>
      )}
    </form>
  );
}

const SIGNALS: [string, (r: OffboardingStatusRow) => OffboardingStatusRow['Phase1Status']][] = [
  ['Phase 1', (r) => r.Phase1Status],
  ['Phase 2', (r) => r.Phase2Status],
  ['OnsiteLive', (r) => r.OnsiteLiveStatus],
  ['Email divert', (r) => r.EmailDivertStatus],
  ['Groups', (r) => r.GroupRemovalStatus],
];

function OffboardingProgress({ personId, userId, username }: { personId: number; userId: number; username: string }) {
  const listQuery = useQuery({ queryKey: ['offboarding-status'], queryFn: ({ signal }) => listOffboardingStatus(signal), retry: false });
  const migrationQuery = useQuery({
    queryKey: ['onedrive-migration', personId],
    queryFn: ({ signal }) => getMigrationForPerson(personId, signal),
  });
  const row = listQuery.data?.Items.find((r) => r.UserId === userId);

  return (
    <section className="rounded border border-border bg-white p-4" aria-label="Offboarding progress">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-lg font-bold text-navy">Offboarding progress</h2>
        <Link to="/offboarding" className="text-xs text-navy hover:underline">All leavers</Link>
      </div>
      {listQuery.isLoading && <p className="mt-2 text-sm text-darkGrey/70">Loading…</p>}
      {listQuery.isError && <p className="mt-2 text-sm text-red">Could not load the offboarding status.</p>}
      {listQuery.data && !row && (
        <p className="mt-2 text-sm text-darkGrey/80">
          {username} is not in the offboarding list yet. They appear there once an expiry is set; the steps run after it passes.
        </p>
      )}
      {row && (
        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <dt className="font-semibold text-darkGrey">Overall</dt>
            <dd><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${overallBadge[row.OverallStatus]}`}>{row.OverallStatus}</span></dd>
          </div>
          {SIGNALS.map(([label, get]) => (
            <div key={label} className="flex items-center gap-2">
              <dt className="font-semibold text-darkGrey">{label}</dt>
              <dd><SignalPill status={get(row)} /></dd>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <dt className="font-semibold text-darkGrey">Effective expiry</dt>
            <dd className="text-darkGrey/80">{formatWallClock(row.EffectiveExpiryDateTime)}</dd>
          </div>
        </dl>
      )}
      {listQuery.data?.MiUnavailable && (
        <p className="mt-2 text-xs text-darkGrey/70">The on-premises steps are unavailable just now; the email divert is current.</p>
      )}
      <h3 className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-darkGrey/70">History</h3>
      <OffboardingTrail userId={userId} username={username} migration={migrationQuery.data ?? undefined} />
    </section>
  );
}
